import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { getEffectiveDatesOptions } from 'global/services-helpers/domainService';
import {
  sunLifeGroupSizeValidation,
  sunLifeSICAdditionalValidations,
  sunLifeSICWarningMessage,
} from 'global/utils/sponsor-validations/sunLife/sicValidation';
import {
  INCORPORATION_DATE_VALIDATION_MSG,
  isIncorporationDateValid,
} from 'global/utils/sponsor-validations/sunLife/incorporationDateValidation';
import { makeAutoObservable, reaction } from 'mobx';
import { ReactNode } from 'react';
import { DomainProductLinesDelegate } from 'store/modules/initiate-quote/domainProductLinesDelegate';
import { getRootStoreInstance, RootStore } from 'store/rootStore';

type SunLifeQuoteProposalInfoFields = {
  proposalName: FormControl<string>;
  effectiveDate: FormControl<string>;
  incorporationDate: FormControl<string>;
  groupSize: FormControl<string>;
  zip: FormControl<string>;
  state: FormControl<string>;
  county: FormControl<string>;
  sic4: FormControl<string>;
};

export class SunLifeQuoteProposalInfoFormDelegate {
  private readonly _rootStore: RootStore = getRootStoreInstance();
  public readonly domainProductLinesDelegate: DomainProductLinesDelegate;
  public proposalForm: FormController<SunLifeQuoteProposalInfoFields>;

  public get effectiveDatesOptions(): OptionType[] {
    return getEffectiveDatesOptions(this._rootStore.domainStoreV2.effectiveDates);
  }

  public get isValid(): boolean {
    return (
      this.proposalForm.basicFieldsValidationsPassed &&
      !this.proposalForm.isLoading &&
      !this.domainProductLinesDelegate.isLoading &&
      // Covered lines are not locked on this page, so this is domain-available GROUP lines only.
      this.domainProductLinesDelegate.hasQuotableProductLine
    );
  }

  /** Shown as a warning `Alert` when the current SIC matches a non-blocking rule. */
  public get sicWarningMessage(): ReactNode | null {
    const sicValue = this.proposalForm.fields.sic4.value;
    return sicValue && sicValue.length === 4 ? sunLifeSICWarningMessage(sicValue) : null;
  }

  public get showIncorporationDate(): boolean {
    return !!this.proposalForm.fields.incorporationDate.value;
  }

  private get _canFetchProductLines(): boolean {
    const { effectiveDate, zip, state, county, sic4 } = this.proposalForm.fields;

    return !!(
      !effectiveDate.unpassedValidationMsg &&
      !zip.unpassedValidationMsg &&
      !state.unpassedValidationMsg &&
      !county.unpassedValidationMsg &&
      !sic4.unpassedValidationMsg
    );
  }

  constructor(domainProductLinesDelegate: DomainProductLinesDelegate) {
    this.domainProductLinesDelegate = domainProductLinesDelegate;
    this.proposalForm = FormController.create(
      {
        proposalName: new FormControl<string>('', {
          validations: ['printableCharacters'],
        }),
        effectiveDate: new FormControl<string>(''),
        groupSize: new FormControl<string>('', {
          customValidations: [sunLifeGroupSizeValidation],
        }),
        incorporationDate: new FormControl<string>('', {
          optional: true,
          enabledWithFields: ['effectiveDate'],
          customValidations: [
            {
              check: (incorporationDate: string): boolean => {
                return isIncorporationDateValid(incorporationDate, this.proposalForm.fields.effectiveDate.value);
              },
              msg: INCORPORATION_DATE_VALIDATION_MSG,
            },
          ],
        }),
      },
      {
        capabilities: [
          {
            type: 'zip',
            params: {
              zip: { optional: false },
              state: {
                customValidations: [
                  {
                    check: this._rootStore.domainStoreV2.isEligibleState,
                    msg: 'State is ineligible',
                  },
                ],
              },
            },
          },
          {
            type: 'sic',
            params: {
              additionalValidations: sunLifeSICAdditionalValidations,
            },
          },
        ],
      },
    );

    reaction(
      () => [
        this.proposalForm.fields.effectiveDate.value,
        this.proposalForm.fields.zip.value,
        this.proposalForm.fields.state.value,
        this.proposalForm.fields.county.value,
        this.proposalForm.fields.sic4.value,
      ],
      async () => {
        if (!this._canFetchProductLines) {
          this.domainProductLinesDelegate.clearProductLines();
          return;
        }

        await this.domainProductLinesDelegate.getDomainProductLines(this.proposalForm.getFormDataObject(), true);
      },
    );

    makeAutoObservable(this, { domainProductLinesDelegate: false }, { autoBind: true });
  }
}
